import { create } from "zustand";

export interface PartnerNotification {
  id: number;
  UpdatedAt: string;
  CreatedAt: string;
  partnerId: number;
  packageName: string;
  platformId: number;
  title: string;
  subtitle: string;
  imageUrl: string;
  scheduledAt: string;
  status: string;
}

interface PackageOption {
  label: string;
  value: string;
}

interface PartnerNotificationStore {
  notifications: any[];
  paginatedData: { [key: number]: any[] };
  shouldRefresh: boolean;
  packageOptions: PackageOption[];
  searchResults: PackageOption[];
  hasMorePackages: boolean;
  currentPackagePage: number;
  searchText: string;
  loadedPages: Set<number>;
  searchCurrentPage: number;
  searchHasMore: boolean;
  hasNextPage: { [key: number]: boolean };
  addNotifications: (
    notifications: any[],
    page: number,
    hasNext: boolean
  ) => void;
  getPageData: (page: number) => any[];
  hasNextPageForPage: (page: number) => boolean;
  setShouldRefresh: (value: boolean) => void;
  clearStore: () => void;
  setPackages: (packages: PackageOption[], page: number) => void;
  appendPackages: (packages: PackageOption[]) => void;
  setSearchResults: (results: PackageOption[]) => void;
  setHasMore: (hasMore: boolean) => void;
  setPage: (page: number) => void;
  setSearchText: (text: string) => void;
  setSearchPage: (page: number) => void;
  setSearchHasMore: (hasMore: boolean) => void;
  appendSearchResults: (results: PackageOption[]) => void;
}

const usePartnerNotificationStore = create<PartnerNotificationStore>(
  (set, get) => ({
    notifications: [],
    paginatedData: {},
    shouldRefresh: false,
    packageOptions: [],
    searchResults: [],
    hasMorePackages: true,
    currentPackagePage: 1,
    searchText: "",
    loadedPages: new Set<number>(),
    searchCurrentPage: 1,
    searchHasMore: true,
    hasNextPage: {},

    addNotifications: (notifications, page, hasNext) =>
      set((state) => ({
        notifications,
        paginatedData: {
          ...state.paginatedData,
          [page]: notifications,
        },
        hasNextPage: {
          ...state.hasNextPage,
          [page]: hasNext,
        },
      })),

    hasNextPageForPage: (page) => {
      const state = get();
      return state.hasNextPage[page] ?? false;
    },

    getPageData: (page) => {
      const state = get();
      const startIndex = (page - 1) * 10;
      const endIndex = startIndex + 10;
      return state.paginatedData[page] || [];
    },

    setShouldRefresh: (value) => set({ shouldRefresh: value }),

    setPackages: (packages, page) =>
      set((state) => ({
        packageOptions:
          page === 1 ? packages : [...state.packageOptions, ...packages],
      })),

    appendPackages: (packages) =>
      set((state) => ({
        packageOptions: [...state.packageOptions, ...packages],
      })),

    setSearchResults: (results) =>
      set({
        searchResults: results,
      }),

    setHasMore: (hasMore) => set({ hasMorePackages: hasMore }),

    setPage: (page) => set({ currentPackagePage: page }),

    setSearchText: (text) =>
      set({
        searchText: text,
        searchResults: [],
        searchCurrentPage: 1,
        searchHasMore: true,
      }),

    clearStore: () =>
      set({
        notifications: [],
        paginatedData: {},
        shouldRefresh: false,
        packageOptions: [],
        searchResults: [],
        hasMorePackages: true,
        currentPackagePage: 1,
        searchText: "",
        loadedPages: new Set<number>(),
        searchCurrentPage: 1,
        searchHasMore: true,
        hasNextPage: {},
      }),

    setSearchPage: (page) => set({ searchCurrentPage: page }),

    setSearchHasMore: (hasMore) => set({ searchHasMore: hasMore }),

    appendSearchResults: (results) =>
      set((state) => ({
        searchResults: [...state.searchResults, ...results],
      })),
  })
);

export default usePartnerNotificationStore;
